import type { Instance, Props } from "tippy.js"
import { ReactRenderer } from "@tiptap/react"
import tippy from "tippy.js"

import type {
	OnKeyDownHandler,
	OnKeyDownHandlerSetter,
	SlashCommandDefinition,
	SlashCommandProps,
	SlashCommandSuggestionImpl,
} from "@/components/editor/extensions/slash-commands/types"
import type { SuggestionProps } from "@tiptap/suggestion"
import {
	bulletedListCommand,
	codeBlockCommand,
	heading1Command,
	heading2Command,
	heading3Command,
	numberedListCommand,
	quoteCommand,
} from "@/components/editor/extensions/slash-commands/commands"
import SlashCommandsList from "@/components/editor/extensions/slash-commands/slash-commands-list"

const items: SlashCommandDefinition[] = [
	heading1Command,
	heading2Command,
	heading3Command,
	numberedListCommand,
	bulletedListCommand,
	quoteCommand,
	codeBlockCommand,
]

// The snippet editor lives in a non-modal Radix Dialog. Appending the tippy
// popup to document.body puts it outside the dialog layer, so the dialog's
// dismiss/focus handling swallows clicks. Mounting it next to the editor keeps
// it inside the layer.
export const snippetSlashSuggestion: SlashCommandSuggestionImpl = {
	items: ({ query }) => {
		const lowerQuery = query.toLocaleLowerCase()
		return items.filter((item) => {
			const titleMatch = item.title.toLocaleLowerCase().includes(lowerQuery)
			const aliasMatch = item.aliases?.some((alias) =>
				alias.toLocaleLowerCase().includes(lowerQuery),
			)
			return titleMatch || aliasMatch
		})
	},
	render: () => {
		let component: ReactRenderer<
			unknown,
			SuggestionProps<SlashCommandDefinition, SlashCommandProps>
		> | null = null
		let popup: Instance<Props>[] | null = null
		let onKeyDownHandler: OnKeyDownHandler = () => true
		const setOnKeyDownHandler: OnKeyDownHandlerSetter = (handler) => {
			onKeyDownHandler = handler
		}

		return {
			onStart: (props) => {
				component = new ReactRenderer(SlashCommandsList, {
					props: { ...props, setOnKeyDownHandler },
					editor: props.editor,
				})

				if (!props.clientRect) return

				// @ts-expect-error tippy types are wrong
				popup = tippy("body", {
					getReferenceClientRect: props.clientRect,
					appendTo: () => props.editor.options.element,
					content: component.element,
					showOnCreate: true,
					interactive: true,
					trigger: "manual",
					placement: "bottom-start",
					zIndex: 9999,
				})
			},
			onUpdate(props) {
				if (!component) return
				component.updateProps(props)
				const { clientRect } = props
				if (!clientRect) return
				popup?.[0]?.setProps({
					getReferenceClientRect: () => {
						const c = clientRect()
						if (!c) throw new Error("clientRect is null")
						return c
					},
				})
			},
			onKeyDown(props) {
				if (props.event.key === "Escape") {
					popup?.[0]?.hide()
					return true
				}
				return onKeyDownHandler(props)
			},
			onExit() {
				popup?.[0]?.destroy()
				component?.destroy()
			},
		}
	},
}
